import React, { useContext, useState } from 'react';
import AuthContext from '../context/AuthContext';
import { MailWarning, Send } from 'lucide-react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * Tarea 027c — banner persistente para cuentas "pendientes de verificar" (mismo patrón
 * visual que ActivationBanner/RenewalBanner). Solo bloquea la suscripción/pago (ver
 * requireVerifiedEmail en el backend) — el resto de la app funciona normal mientras se
 * muestra este aviso. Incluye reenvío del correo y corrección del email si hubo un typo.
 */
const EmailVerificationBanner = () => {
    const { user, applyAuthTokens, authTokens } = useContext(AuthContext);
    const { t } = useTranslation();
    const [showEditEmail, setShowEditEmail] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState('');

    if (!user || user.email_verified_at) return null;

    const handleResend = async () => {
        setResendStatus('sending');
        try {
            await api.post('/auth/resend-verification', { email: user.email });
        } finally {
            // Respuesta genérica anti-enumeración: siempre "enviado".
            setResendStatus('sent');
        }
    };

    const handleEditEmail = async (e) => {
        e.preventDefault();
        setEditError('');
        setIsSaving(true);
        try {
            const response = await api.put('/auth/pending-email', { email: newEmail });
            // Mismo patrón que switchBusiness: solo cambia el access token, el refresh
            // token de la sesión actual se conserva.
            await applyAuthTokens({ token: response.data.token, refreshToken: authTokens?.refreshToken });
            setShowEditEmail(false);
            setNewEmail('');
            setResendStatus('sent'); // el backend ya reenvió al correo corregido
        } catch (err) {
            setEditError(err.response?.data?.message || t('emailVerificationBanner.editFailed'));
        } finally {
            setIsSaving(false);
        }
    };

    const bannerStyle = {
        background: 'linear-gradient(90deg, #0369a1 0%, #0284c7 100%)',
        color: 'white',
        borderBottom: '1px solid #075985',
        position: 'relative',
        zIndex: 1,
    };

    const iconContainerStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: '0.5rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const ctaStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        backgroundColor: 'white',
        color: '#0369a1',
        fontWeight: 'bold',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        border: 'none',
    };

    return (
        <div style={bannerStyle} className="shadow-sm">
            <Container className="py-3">
                <Row className="align-items-center gy-2">
                    <Col md={7} className="d-flex align-items-start align-items-md-center gap-3">
                        <div style={iconContainerStyle} className="flex-shrink-0">
                            <MailWarning size={24} color="white" />
                        </div>
                        <div>
                            <h5 className="mb-1 fw-bold">{t('emailVerificationBanner.title')}</h5>
                            <p className="mb-0 small text-white">
                                {t('emailVerificationBanner.description', { email: user.email })}
                            </p>
                        </div>
                    </Col>

                    <Col md={5}>
                        <div className="d-flex flex-column align-items-md-end gap-2">
                            <div className="d-flex gap-2 justify-content-md-end align-items-center flex-wrap">
                                {resendStatus === 'sent' ? (
                                    <span className="small">{t('emailVerificationBanner.resendSent')}</span>
                                ) : (
                                    <button
                                        id="onboarding-verify-email"
                                        type="button"
                                        style={ctaStyle}
                                        onClick={handleResend}
                                        disabled={resendStatus === 'sending'}
                                    >
                                        <Send size={16} />
                                        <span className="small">
                                            {resendStatus === 'sending'
                                                ? t('emailVerificationBanner.resendSending')
                                                : t('emailVerificationBanner.resendButton')}
                                        </span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-white text-decoration-none"
                                    onClick={() => setShowEditEmail((v) => !v)}
                                >
                                    {t('emailVerificationBanner.wrongEmailToggle')}
                                </button>
                            </div>

                            {showEditEmail && (
                                <Form
                                    onSubmit={handleEditEmail}
                                    className="d-flex gap-2 align-items-start flex-wrap justify-content-md-end"
                                >
                                    <Form.Control
                                        type="email"
                                        size="sm"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        placeholder={t('emailVerificationBanner.newEmailPlaceholder')}
                                        required
                                        style={{ maxWidth: '220px' }}
                                    />
                                    <Button type="submit" size="sm" variant="light" disabled={isSaving}>
                                        {isSaving ? t('emailVerificationBanner.saving') : t('emailVerificationBanner.saveButton')}
                                    </Button>
                                </Form>
                            )}
                            {editError && <span className="small text-white">{editError}</span>}
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default EmailVerificationBanner;
